import { SimpleLayoutElement } from "@nms-ng2/app/shared/services/layout-manager/layout-manager.models";
import { TOPOLOGY_SETTINGS } from "../models/diagram.constants";
import {
    MuxElement,
    CircuitElementType,
    TransponderElement,
    CircuitElementState,
    AmplifierElement,
    OscElement,
    DeviceElement
} from "../models/diagram.models";
import { TopologySettings } from "./circuit-layout.models";
import { WHITE_COLOR } from "@nms-ng2/app/shared/constants/colors";

/**
 * Classe auxiliar para criar os parâmetros iniciais necessários para os elementos do CircuitLayoutBuilder.
 */
export class CircuitElementParameters {

    /**
     * Constrói os parâmetros iniciais para um elemento de layout de forma genérica.
     *
     * @param params parâmetros padrão para todos elementos de topologia.
     * @param data elemento de layout parcialmente preenchido.
     * @returns SimpleLayoutElement com as propriedades do elemento desejado.
     */
    static buildParameters(params: TopologySettings, data: SimpleLayoutElement): SimpleLayoutElement {
        const layoutElement: SimpleLayoutElement = { ...data };

        layoutElement.style.image = params.imagePath;
        layoutElement.width = params.width;
        layoutElement.height = params.height;

        return layoutElement as SimpleLayoutElement;
    }

    /**
     * Constrói os parâmetros iniciais de um elemento de layout de um MUX/DEMUX.
     * Adquire os parâmetros iniciais de layout a partir do mapeamento definido na constante {@see TOPOLOGY_SETTINGS},
     * acessada pelo tipo do elemento (i.e. Mux) e pelo estado atual do elemento.
     *
     * @param element Mux
     * @returns SimpleLayoutElement com as propriedades de um MUX/DEMUX.
     */
    static buildMuxParameters(element: MuxElement): SimpleLayoutElement {
        const data: SimpleLayoutElement = { style: {shape: "image"} };
        const params = TOPOLOGY_SETTINGS[CircuitElementType.MUX][element.state];

        return CircuitElementParameters.buildParameters(params, data);
    }

    /**
     * Constrói os parâmetros iniciais de um elemento de layout de um transponder.
     * Adquire os parâmetros iniciais de layout a partir do mapeamento definido na constante {@see TOPOLOGY_SETTINGS}.
     * Caso se trate de um transponder não configurado, a constante é acessada pelo tipo do elemento (i.e. Transponder),
     * e pelo estado atual do elemento. Caso constrário, além do tipo e estado do elemento, também é usado o multirate
     * do transponder.
     *
     * @param element Transponder
     * @returns SimpleLayoutElement com as propriedades de um transponder.
     */
    static buildTransponderParameters(element: TransponderElement): SimpleLayoutElement {
        const data: SimpleLayoutElement = { style: {shape: "image"} };

        const transponderParamsMap = TOPOLOGY_SETTINGS[CircuitElementType.TRANSPONDER];

        const params: TopologySettings = (element.state === CircuitElementState.CONFIGURED)
            ? transponderParamsMap[element.state][element.multirate]
            : transponderParamsMap[element.state];

        return CircuitElementParameters.buildParameters(params, data);
    }

    /**
     * Constrói os parâmetros iniciais de um elemento de layout de um amplificador.
     * Adquire os parâmetros iniciais de layout a partir do mapeamento definido na constante {@see TOPOLOGY_SETTINGS},
     * acessada pelo tipo do elemento (i.e. Amplifier), pelo tipo do amplificador (i.e. Booster, PreAmp)
     * e pelo estado atual do elemento.
     *
     * @param element Amplificador
     * @returns SimpleLayoutElement com as propriedades de um amplificador.
     */
    static buildAmplifierParameters(element: AmplifierElement): SimpleLayoutElement {
        const data: SimpleLayoutElement = { style: {shape: "image"} }
        const params = TOPOLOGY_SETTINGS[CircuitElementType.AMPLIFIER][element.type][element.state];

        return CircuitElementParameters.buildParameters(params, data);
    }

    /**
     * Constrói os parâmetros iniciais de um elemento de layout de um OSC.
     * Adquire os parâmetros iniciais de layout a partir do mapeamento definido na constante {@see TOPOLOGY_SETTINGS},
     * acessada pelo tipo do elemento (i.e. OSC) e pelo estado atual do elemento.
     *
     * @param element OSC
     * @returns SimpleLayoutElement com as propriedades de um OSC.
     */
    static buildOscParameters(element: OscElement): SimpleLayoutElement {
        const data: SimpleLayoutElement =
            { style: { shape: "image", fontColor: WHITE_COLOR, fontSize: 10 } };
        const params = TOPOLOGY_SETTINGS[CircuitElementType.OSC][element.state];

        return CircuitElementParameters.buildParameters(params, data);
    }

    /**
     * Constrói os parâmetros iniciais de um elemento de layout de um equipamento.
     * Adquire os parâmetros iniciais de layout a partir do mapeamento definido na constante {@see TOPOLOGY_SETTINGS},
     * acessada pelo tipo do elemento (i.e. Device) e pelo tipo do equipamento (i.e. END, INTERMEDIATE_FULL, INTERMEDIATE_HALF).
     *
     * @param element Equipamento
     * @returns SimpleLayoutElement com as propriedades de um equipamento.
     */
    static buildDeviceParameters(element: DeviceElement): SimpleLayoutElement {
        const data: SimpleLayoutElement = { style: { shape: "image", imageAspect: false} };
        const params = TOPOLOGY_SETTINGS[CircuitElementType.DEVICE][element.type];

        return CircuitElementParameters.buildParameters(params, data);
    }
}
